import { useEffect, useState } from 'react';
import type { SocialClient } from '@versesverse/core';

/*
 * WHO IS ON THE MAP RIGHT NOW.
 *
 * One question, asked of the social plane every so often, answered in counts. The map draws a
 * figure somebody is standing at differently — a ring of seats around it, and a pulse when one of
 * them has a live voice — and it needs every figure at once to do that.
 *
 * POLLED RATHER THAN PUSHED, and that is a deliberate choice rather than a shortcut. A socket for
 * this would hold a connection open for every reader looking at the map, to deliver a number that
 * changes when somebody walks into a room — and the map is the page a reader leaves open. Polling
 * costs one small request a minute and nothing at all in between.
 *
 * IT NEVER FAILS LOUDLY. A deployment with no social plane, a plane that is down, a reader offline:
 * all of them are a map with nobody on it, which is a perfectly ordinary map and not an error state
 * worth a message. The presence layer simply draws nothing.
 */

/** How many readers are at a figure, and whether one of them has a live voice. */
export interface Presence {
  readers: number;
  voice: boolean;
}

/**
 * HOW OFTEN IT IS ASKED.
 *
 * A minute. Presence is the slowest-moving thing the map draws — somebody joins a room and stays
 * for half an hour — and the seats are an ambient signal rather than a control anybody acts on. A
 * faster poll would buy a reader nothing they would notice and cost every open tab a request.
 */
const EVERY_MS = 60_000;

/**
 * The presence of every figure that has any, by node id.
 *
 * `client` may be absent — a deployment with no identity plane has no rooms to count — and the
 * answer is then simply an empty map, which every layer already knows how to draw.
 */
export function usePresence(client: SocialClient | null, enabled: boolean): ReadonlyMap<string, Presence> {
  const [presence, setPresence] = useState<ReadonlyMap<string, Presence>>(() => new Map());

  useEffect(() => {
    if (client === null || !enabled) {
      setPresence(new Map());
      return;
    }
    let live = true;
    const ask = (): void => {
      void client.mapPresence().then(
        rows => {
          if (!live) return;
          setPresence(new Map(rows.map(r => [r.nodeId, { readers: r.readers, voice: r.voice }])));
        },
        () => {
          /* Nobody, rather than an error: see the note above. The next poll may well succeed. */
          if (live) setPresence(new Map());
        },
      );
    };
    ask();
    const timer = window.setInterval(ask, EVERY_MS);
    return (): void => {
      live = false;
      window.clearInterval(timer);
    };
  }, [client, enabled]);

  return presence;
}
